/**
 * ObservationReview — Observation evidence review page.
 *
 * Mirrors the design language of the SSI Review page (CypAsdSSIReview):
 *
 *   1. A centred summary table with one row per DSM-5 symptom (A1–B4).
 *      For each symptom, four columns show which tasks contributed
 *      "Clear evidence", "Partial evidence", "No evidence", or
 *      "Inconclusive" — rendered as compact bullet-point task lists.
 *
 *   2. A confidence level segmented bar (0–100% in 10% increments)
 *      with a traffic-light colour gradient from red to green.
 *
 *   3. A StructuredNotesEditor for formulation / synthesis notes.
 *
 * Data is aggregated from `allTaskData` — each task stores per-criterion
 * evidence as `dsm_<criterionId>_evidence`. Extra clinician-added criteria
 * (stored in `dsm_extraCriteria`) are also included.
 *
 * Review-specific state (confidence + notes) is persisted independently
 * to localStorage so it survives navigation between tasks and review.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { XIcon, PlusIcon } from '@heroicons/react/solid';
import StructuredNotesEditor from './StructuredNotesEditor';
import ObservationEvidenceTables, { ObservationEmptyState } from './ObservationEvidenceTables';
import { EMPTY_OBS_REVIEW_DATA, ensureSeedData } from '../../seedData/cypAsdSeedData';

/**
 * Curated conditions list for the Formulation panel. Autism is pinned to the
 * top since it's the primary condition under assessment; the remaining entries
 * cover the most common differentials and co-occurring conditions seen in CYP
 * ASD pathways. The full ICD-11 list lives in data_DF.js — this is a
 * streamlined subset for the mockup.
 */
const FORMULATION_CONDITIONS = [
  'Autism Spectrum Disorder (ASD)',
  'Attention Deficit Hyperactivity Disorder (ADHD)',
  'Social Anxiety Disorder',
  'Generalised Anxiety Disorder',
  'Developmental Speech or Language Disorders',
  'Developmental Learning Disorder',
  'Disorders of Intellectual Development',
  'Reactive Attachment Disorder',
  'Obsessive-Compulsive Disorder',
  'Post-Traumatic Stress Disorder',
  'Depressive Disorders',
  'Personality Disorder',
];

const CONFIDENCE_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export default function ObservationSummary({
  tasks,
  selectedTaskIds,
  allTaskData,
  confidenceStyle = 'ternary',
  adosData = null,
}) {
  const { patientId } = useParams();
  useEffect(() => { ensureSeedData(patientId); }, [patientId]);
  const reviewKey = `cyp-asd-obs-review-${patientId}`;

  // ── Review-specific persisted state ──────────────────────────────────

  const savedReview = useMemo(() => {
    try {
      const cached = localStorage.getItem(reviewKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_OBS_REVIEW_DATA;
  }, [reviewKey]);

  const [confidence, setConfidence] = useState(() => savedReview?.confidence ?? null);
  const [confidenceTernary, setConfidenceTernary] = useState(() => savedReview?.confidenceTernary ?? null);
  const [formulationNotes, setFormulationNotes] = useState(() => {
    const raw = savedReview?.formulationNotes;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
    return [];
  });
  const handleNotesChange = useCallback((val) => setFormulationNotes(val), []);

  /** Formulation — which conditions the clinician is considering + per-condition notes. */
  const [formulationConditions, setFormulationConditions] = useState(
    () => savedReview?.formulationConditions ?? [],
  );
  const [formulationConditionNotes, setFormulationConditionNotes] = useState(
    () => savedReview?.formulationConditionNotes ?? {},
  );

  /**
   * Tracks conditions that are in the process of being removed — they stay
   * in the list long enough to play the exit animation, then get purged.
   */
  const [leavingConditions, setLeavingConditions] = useState(new Set());

  const toggleCondition = useCallback((condition) => {
    if (formulationConditions.includes(condition)) {
      setLeavingConditions(prev => new Set(prev).add(condition));
    } else {
      setFormulationConditions(prev => [...prev, condition]);
    }
  }, [formulationConditions]);

  const finishLeaving = useCallback((condition) => {
    setLeavingConditions(prev => {
      const next = new Set(prev);
      next.delete(condition);
      return next;
    });
    setFormulationConditions(prev => prev.filter(c => c !== condition));
  }, []);

  const handleConditionNoteChange = useCallback((condition, value) => {
    setFormulationConditionNotes(prev => ({ ...prev, [condition]: value }));
  }, []);

  /**
   * Moves a note card from one formulation condition to another. The
   * StructuredNotesEditor fires this when a note is dragged between rows,
   * enabling clinicians to quickly reorganise thoughts across conditions.
   */
  const handleMoveFormulationNote = useCallback((sourceCondition, noteId, targetCondition, insertAt) => {
    setFormulationConditionNotes(prev => {
      const sourceNotes = Array.isArray(prev[sourceCondition]) ? prev[sourceCondition] : [];
      const targetNotes = Array.isArray(prev[targetCondition]) ? prev[targetCondition] : [];
      const note = sourceNotes.find(n => n.id === noteId);
      if (!note) return prev;
      const updated = [...targetNotes];
      if (insertAt != null && insertAt <= updated.length) {
        updated.splice(insertAt, 0, note);
      } else {
        updated.push(note);
      }
      return {
        ...prev,
        [sourceCondition]: sourceNotes.filter(n => n.id !== noteId),
        [targetCondition]: updated,
      };
    });
  }, []);

  /** "Other" condition — tracks whether the inline input is open and its draft text. */
  const [otherInputOpen, setOtherInputOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState('');
  const otherInputRef = useRef(null);

  /**
   * Custom conditions typed via the "Other" input. These are rendered as
   * additional pill buttons alongside the pre-defined conditions so the
   * user can toggle them on/off just like any built-in condition.
   */
  const [customConditions, setCustomConditions] = useState(
    () => savedReview?.customConditions ?? [],
  );

  const confirmOtherCondition = useCallback(() => {
    const trimmed = otherDraft.trim();
    if (trimmed && !formulationConditions.includes(trimmed) && !FORMULATION_CONDITIONS.includes(trimmed)) {
      setCustomConditions(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      setFormulationConditions(prev => [...prev, trimmed]);
    }
    setOtherDraft('');
    setOtherInputOpen(false);
  }, [otherDraft, formulationConditions]);

  useEffect(() => {
    try {
      localStorage.setItem(reviewKey, JSON.stringify({
        confidence, confidenceTernary, formulationNotes,
        formulationConditions, formulationConditionNotes, customConditions,
      }));
    } catch { /* quota exceeded — ignore in mockup */ }
  }, [reviewKey, confidence, confidenceTernary, formulationNotes, formulationConditions, formulationConditionNotes, customConditions]);

  // ── Empty state ──────────────────────────────────────────────────────

  if (selectedTaskIds.length === 0) {
    return <ObservationEmptyState />;
  }

  // ── Main render ──────────────────────────────────────────────────────

  return (
    <div className="flex-grow p-6 text-left">
      <div className="flex flex-col items-center gap-6">

        {/* Page header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Review of Behavioural Observation
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Evidence aggregated across {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} by DSM criteria.
          </p>
        </div>

        {/* ======== Evidence summary tables (DSM + ADOS) ======== */}
        <ObservationEvidenceTables
          tasks={tasks}
          selectedTaskIds={selectedTaskIds}
          allTaskData={allTaskData}
          adosData={adosData}
        />

        {/* ======== Review panel (Confidence) ======== */}
        <div className="w-full max-w-5xl bg-gray-50 rounded-2xl border border-gray-200 p-5 space-y-1">

          {/* Confidence Level */}
          <div className="pt-1">
            <h3 className="text-sm font-bold text-gray-800 mb-0.5">
              Confidence Level<span className="text-red-500 ml-0.5">*</span>
            </h3>
            <p className="text-xs text-gray-500 mb-2">
              Based on the Behavioural Observation, how likely is it in your clinical opinion that this person is autistic?
            </p>

            {confidenceStyle === 'percentage' ? (
              <div className="flex items-center gap-0.5 mb-2">
                {CONFIDENCE_STEPS.map((step) => {
                  const isSelected = confidence !== null && step <= confidence;
                  const isExact = step === confidence;

                  let selectedColour = 'bg-red-400';
                  if (step >= 40 && step < 70) selectedColour = 'bg-amber-400';
                  if (step >= 70) selectedColour = 'bg-emerald-400';

                  return (
                    <button
                      key={step}
                      onClick={() => setConfidence(confidence === step ? null : step)}
                      className={`
                        flex-1 h-7 first:rounded-l-md last:rounded-r-md
                        font-bold transition-all duration-200
                        ${isSelected
                          ? `${selectedColour} text-white`
                          : 'bg-gray-200 text-gray-400 hover:bg-gray-300'}
                        ${isExact ? 'ring-2 ring-offset-1 ring-primary z-10 relative text-xs' : 'text-[10px]'}
                      `}
                      title={`${step}%`}
                    >
                      {step}%
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-2">
                {[
                  { value: 'very_unlikely', label: 'Very Unlikely',     activeBg: 'bg-red-100 border-red-400 text-red-700',         hoverBorder: 'hover:border-red-300' },
                  { value: 'unlikely',      label: 'Unlikely',          activeBg: 'bg-orange-100 border-orange-400 text-orange-700', hoverBorder: 'hover:border-orange-300' },
                  { value: 'likely',        label: 'Likely',            activeBg: 'bg-lime-100 border-lime-400 text-lime-700',       hoverBorder: 'hover:border-lime-300' },
                  { value: 'very_likely',   label: 'Very Likely',       activeBg: 'bg-emerald-100 border-emerald-400 text-emerald-700', hoverBorder: 'hover:border-emerald-300' },
                ].map(opt => {
                  const isActive = confidenceTernary === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setConfidenceTernary(isActive ? null : opt.value)}
                      className={`
                        flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all duration-200
                        ${isActive
                          ? opt.activeBg
                          : `bg-white border-gray-300 text-gray-500 ${opt.hoverBorder} hover:text-gray-700`}
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* ======== Formulation — conditions under consideration + notes ======== */}
        <div className="w-full max-w-5xl bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-0.5">Differentials</h3>
          <p className="text-xs text-gray-500 mb-3">
            Select any conditions you are currently considering. Add notes for each to capture your clinical reasoning.
          </p>

          {/* Condition picker — pill buttons + "Other" inline input */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[...FORMULATION_CONDITIONS, ...customConditions].map(condition => {
              const isSelected = formulationConditions.includes(condition) && !leavingConditions.has(condition);
              return (
                <button
                  key={condition}
                  onClick={() => toggleCondition(condition)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                    border transition-all duration-200
                    ${isSelected
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-primary/50 hover:text-primary'}
                  `}
                >
                  {!isSelected && <PlusIcon className="w-3 h-3" />}
                  {isSelected && (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {condition}
                </button>
              );
            })}

            {/* "Other" button — morphs into an inline text input when clicked */}
            <div
              className={`
                inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold border
                transition-all duration-300 overflow-hidden
                ${otherInputOpen
                  ? 'bg-primary/10 border-primary text-primary px-2 py-0.5'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-primary/50 hover:text-primary px-3 py-1.5 cursor-pointer'}
              `}
              onClick={() => {
                if (!otherInputOpen) {
                  setOtherInputOpen(true);
                  requestAnimationFrame(() => otherInputRef.current?.focus());
                }
              }}
            >
              {!otherInputOpen && <PlusIcon className="w-3 h-3" />}
              {!otherInputOpen && <span>Other</span>}
              {otherInputOpen && (
                <input
                  ref={otherInputRef}
                  type="text"
                  value={otherDraft}
                  onChange={(e) => setOtherDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); confirmOtherCondition(); }
                    if (e.key === 'Escape') { setOtherDraft(''); setOtherInputOpen(false); }
                  }}
                  onBlur={() => { if (otherDraft.trim()) confirmOtherCondition(); else setOtherInputOpen(false); }}
                  placeholder="Type condition name…"
                  className="bg-transparent text-xs font-semibold text-primary placeholder-primary/40
                    border-0 outline-none w-48 py-1"
                />
              )}
            </div>
          </div>

          {/* Selected conditions — two-column grid with animated rows */}
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              display: 'grid',
              gridTemplateRows: formulationConditions.length > 0 ? '1fr' : '0fr',
              opacity: formulationConditions.length > 0 ? 1 : 0,
            }}
          >
            <div className="min-h-0">
              <div className="border-t border-gray-200 pt-4 space-y-3">
                {formulationConditions.map(condition => {
                  const isLeaving = leavingConditions.has(condition);
                  return (
                    <div
                      key={condition}
                      className={`grid gap-4 items-start ${
                        isLeaving
                          ? 'animate-[fadeSlideOut_0.2s_ease-in_forwards]'
                          : 'animate-[fadeSlideIn_0.25s_ease-out]'
                      }`}
                      style={{ gridTemplateColumns: '220px 1fr' }}
                      onAnimationEnd={() => { if (isLeaving) finishLeaving(condition); }}
                    >
                      <div className="flex items-center gap-2 pt-1.5">
                        <button
                          onClick={() => toggleCondition(condition)}
                          className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                          title="Remove condition"
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-sm font-semibold text-gray-800 leading-snug">{condition}</span>
                      </div>
                      <StructuredNotesEditor
                        notes={formulationConditionNotes[condition] || []}
                        onChange={(val) => handleConditionNoteChange(condition, val)}
                        placeholder="Type a note and press Enter…"
                        minHeight="2.5rem"
                        domainId={condition}
                        onMoveNoteIn={handleMoveFormulationNote}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ======== Confidential Case Notes — separate panel ======== */}
        <div
          className="w-full max-w-5xl rounded-2xl border border-gray-200 p-5"
          style={{ backgroundColor: '#ced6f6' }}
        >
          <h3 className="text-sm font-bold text-gray-800 mb-0.5">Confidential Case Notes</h3>
          <p className="text-xs text-gray-500 italic mb-2">These notes will not be used as inputs for AI-generated drafts or included in the patient report.</p>
          <StructuredNotesEditor
            notes={formulationNotes}
            onChange={handleNotesChange}
            placeholder="Record clinical reasoning, observation synthesis, or differential considerations…"
            rows={6}
          />
        </div>

      </div>

      {/* Keyframes for animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideOut {
          from { opacity: 1; transform: translateY(0); max-height: 200px; }
          to   { opacity: 0; transform: translateY(-6px); max-height: 0; }
        }
      `}</style>
    </div>
  );
}
